import setVehicleFromGetPageDetails from "action/garage/setVehicleFromGetPageDetails";
import setVehicleImageUrl from "action/garage/setVehicleImageUrl";
import { setPageType } from "action/utils/setPageType";
import { setUrlInfo } from "action/utils/setUrlInfo";
import { IPageContent } from "api/interfaces/static-content.type";
import LoaderSkeleton from "component/loader-skeleton/LoaderSkeleton";
import usePreserveScroll from "hoc/usePreserveScroll";
import useSafeEffect from "hooks-contexts/useSafeEffect";
import { RootState } from "interfaces/generics.type";
import { COMPETITOR_PAGES, PAGE_WITH_HP_UI } from "modules/static-pages/static-page-constants";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { wrapper } from "store";
import PASentry from "util/pa-sentry";
import nextI18nextConfig from "../../next-i18next.config";
import { addVehicle } from "../action/garage/addVehicle";
import { clearSelection } from "../action/garage/clearSelection";
import { setVehicleDefaultValue } from "../action/garage/setVehicleDefaultValue";
import PagePlaceholder, { PLACEHOLDER_PAGE_TYPE } from "../component/category/PagePlaceholder";
import { sendProductViewEvent } from "../util/analytics/sendProductViewEvent";
import { trackEvents } from "../util/analytics/trackEvents";
import { TRACKING_EVENT_NAME } from "../util/analytics/tracking.service";
import GlobalUtil from "../util/global.util";
import { deepFind } from "../util/helper/deepFind";
import { pick } from "../util/lodash/pick";
import {
  COLLECTION_PAGE_TYPES, LINE_PAGES, NON_VEHICLE_PAGE_TYPES, PAGE_TYPES, PRODUCT_PAGE_TYPES, TIER_MAKE_PAGES,
} from "../util/product/product-constants";

// const ProductContainer = dynamic(
//   () => import(/* webpackChunkName: "product-container" */ "../modules/product/ProductContainer"),
//   { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
// ); // 72KB

const ProductLineContainer = dynamic(
  () => import(/* webpackChunkName: "product-line-container" */ "../modules/product/ProductLineContainer"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
); // 72KB

// import CategoryTierContainer from "../modules/category/CategoryTierContainer"; // 57.64KB
const CategoryTierContainer = dynamic(
  () => import(/* webpackChunkName: "category-container" */ "../modules/category/CategoryTierContainer"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
); // 53.78KB

const ManufacturerTierContainer = dynamic(
  () => import(/* webpackChunkName: "manufacturer-container" */"../container/manufacturer-tier/ManufacturerTierContainer"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
);

const MakeMakeModelPage = dynamic(
  () => import(/* webpackChunkName: "make-make-model-container" */ "../modules/site-structure/MakeMakeModelPage"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.MAKE_MODEL} /> },
);

const NotFound = dynamic(
  () => import(/* webpackChunkName: "notfound" */"component/notfound/notfound.component"),
  { ssr: false },
);

const StaticPage = dynamic(() => import(/* webpackChunkName: "static-pages" */"modules/static-pages/Index"),
  { loading: () => <LoaderSkeleton height={1200} /> });

const ManufacturerContainer = dynamic(() => import(/* webpackChunkName: "manufacture-container" */"../container/manufacturer/ManufacturerContainer"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.MAKE_MODEL} /> });

const LandingPage = dynamic(
  () => import(/* webpackChunkName: "landing-page" */"component/landing-page"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CMS_PAGE} /> },
);

const MakeModelYearPage = dynamic(() => import(/* webpackChunkName: "make-model-year-page" */ "../modules/site-structure/MakeModelYearPage"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.MAKE_MODEL} /> });

const MakeTierPage = dynamic(() => import(/* webpackChunkName: "make-tier-page" */ "../modules/site-structure/MakeTierPage"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.MAKE_MODEL} /> });

const AllMakeManufacture = dynamic(() => import(/* webpackChunkName: "year-make-model" */ "../modules/site-structure/AllMakeManufacture"),
  { loading: () => <LoaderSkeleton height={1200} /> });

const ProductContainerWrapper = dynamic(
  () => import(/* webpackChunkName: "product-container" */ "modules/product/ProductContainerWrapper"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.PRODUCT_PAGE} /> },
); // 53.78KB

interface IProps {
  staticContent: IPageContent;
}

function NoMatch(props: IProps) {
  const {
    actualResponse,
    pageType,
    pageNotFound,
    staticContent,
    isSSR,
    staticData,
    urlInformation,
    serverRequestUrl,
    ssrVehicleImageUrl,
    locale,
  } = props;

  const dispatch = useDispatch();
  const router = useRouter();
  const { pathname } = router.query;

  const { selectedVehicle, vehicleImageUrl } = useSelector((state: RootState) => state.garage);
  usePreserveScroll();

  useEffect(() => {
    if (props.reloadPage) {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    if (!vehicleImageUrl && ssrVehicleImageUrl) {
      dispatch(setVehicleImageUrl(ssrVehicleImageUrl));
    }
  }, [vehicleImageUrl, ssrVehicleImageUrl]);

  useSafeEffect(() => {
    if (pageType === "REDIRECT") {
      router.replace(props.href, props.asPath);
      return;
    }
    if (actualResponse && pageType !== PAGE_TYPES.YEAR_MAKE_MODEL_PAGE) { // To open vehicle selector when click on change vehicle button without prefilled data
      const deafultObj = {
        defaultMake: urlInformation?.make,
        defaultModel: urlInformation?.model,
        defaultYear: urlInformation?.year,
        // These are delete from BE but passed same value
        displayableMake: urlInformation?.make,
        displayableModel: urlInformation?.model,
      };
      dispatch(setVehicleDefaultValue(deafultObj));
    }
  }, [pageType]);

  useEffect(() => {
    const { vehicleSelectionOptions = {} } = actualResponse || {};
    dispatch(setVehicleFromGetPageDetails(vehicleSelectionOptions));
  }, [actualResponse?.vehicleSelectionOptions]);

  // When locale is changed, clear all button does not work because pageType & urlInformation is unset while unmounting.
  // vehicle-details API also does not work because tier0 is not present
  useSafeEffect(() => {
    dispatch(setPageType(pageType));
    // sometimes urlInformation is null, like in case of REDIRECT. So, add {} as default value
    // Reproduce:
    // On mobile, go to https://partsavatar.ca/fr/hyundai-elantra-replacement-parts?year=2017
    // click on categories & then on replacement parts
    // during redirect, urlInformation is null, so modal remains open & page crashes
    let otherUrlInfo = {};
    if (PRODUCT_PAGE_TYPES.includes(pageType)) {
      // Extended Info category is needed on product page to open submodel, trim, engine in fitment
      const { product } = actualResponse;
      otherUrlInfo = pick(product.primaryCategory, ["tier0", "tier1", "tier2", "tier3"]);
    }

    dispatch(setUrlInfo({ ...urlInformation, ...otherUrlInfo }));
  }, [urlInformation]);

  useSafeEffect(() => () => {
    // componentwillunmount
    dispatch(setPageType(null));
    // unset extended info on unmount
    dispatch(setUrlInfo({}));
  }, []);

  useSafeEffect(() => {
    if (pageType === PAGE_TYPES.YEAR_MAKE_MODEL_PAGE) {
      dispatch(addVehicle(urlInformation));
    }
    if (NON_VEHICLE_PAGE_TYPES.includes(pageType)) {
      dispatch(clearSelection());
    }
  }, []);

  useSafeEffect(() => {
    try {
      let category = {};

      if (PRODUCT_PAGE_TYPES.includes(pageType) && actualResponse?.product) {
        category = actualResponse?.product?.category || {};
        // http://localhost:3000/2002-2007-jeep-truck-liberty-blower-motor-resistor-dorman-techoice-973-425
      } else if ((COLLECTION_PAGE_TYPES.includes(pageType) || TIER_MAKE_PAGES.includes(pageType))
        && staticContent.pageDetails) {
        const tier0 = urlInformation.tier0 || "";
        const tier1 = urlInformation.tier1 || "";
        const tier2 = urlInformation.tier2 || "";
        const tier3 = urlInformation.tier3 || "";
        category = {
          tier0,
          tier1,
          tier2,
          tier3,
        };
      }
      window.pageDetails = {
        pageType,
        category,
      };
    } catch (e) {
      PASentry.logException(event);
    }

    if (props.pageType === PAGE_TYPES.PRODUCT_FITMENT) {
      const vehicle = pick(props.urlInformation || {}, ["make", "model", "year"]);
      dispatch(addVehicle(vehicle));
      trackEvents(
        TRACKING_EVENT_NAME.VEHICLE_SELECTED,
        "select",
        { ...vehicle, selectionType: "AUTOMATIC" },
      );
    }
    if ([PAGE_TYPES.PRODUCT, PAGE_TYPES.PRODUCT_FITMENT].includes(props.pageType)) {
      sendProductViewEvent(
        props.pageType,
        actualResponse,
        selectedVehicle || {},
      );

      // If the user is navigating from any page to our product page
      // we save the product he clicked in order to scroll back to it when navigating
      // back and forth ( currently being used by category, search and t0/t1)
      const history = GlobalUtil.get("history") || [];
      const index = history.findIndex((obj: any) => obj.url === pathname);
      if (index !== -1) {
        history.splice(index, 1);
      } else if (history.length >= 20) {
        // max history size
        history.splice(0, 1);
      }
      history.push({ url: pathname });
      GlobalUtil.set("history", history);
    }
  }, [pathname]);

  if (pageNotFound || actualResponse === null) {
    return <NotFound />;
  }

  if (props.reloadPage) {
    return null;
  }

  switch (pageType) {
  /** *************** Static PAGES **************** */
  case "REDIRECT":
    return (
      <>
        <Skeleton height={38} count={1} />
        <Skeleton height={30} count={1} />
        <Skeleton height={100} count={1} />
        <Skeleton height={43} count={1} />
        <Skeleton height={45} count={3} />
        <Skeleton height={200} count={2} />
      </>
    );
  case "STATIC_PAGE":
    return (
      <StaticPage
        staticData={staticData}
        actualResponse={actualResponse}
        pageType={pageType}
      />
    );
    /** *************** PRODUCT PAGES **************** */
  case PAGE_TYPES.PRODUCT:
  case PAGE_TYPES.PRODUCT_FITMENT:
  case PAGE_TYPES.PRODUCT_MAKE_MODEL_PAGE: {
    const data = actualResponse;
    return (
      <ProductContainerWrapper
        {...{
          staticContent, pageType, actualResponse: data, serverRequestUrl, locale,
        }}
      />
    );
  }
  case PAGE_TYPES.PRODUCT_LINE_PAGE:
  case PAGE_TYPES.MAKE_MODEL_PRODUCT_LINE_PAGE: {
    const data = actualResponse;
    // /chevrolet-silverado-2500hd-weathertech-black-floor-mat
    return (
      <ProductLineContainer
        {...{
          staticContent, pageType, actualResponse: data, isSSR,
        }}
        {...pick(actualResponse, [
          "make",
          "model",
          "displayableMake",
          "displayableModel",
        ])}
      />
    );
  }
  /** *************** Tier Category/Collection PAGES **************** */
  // staticContent is pageContent
  case "TIER0_PAGE":
  case "TIER0_YEAR_MAKE_MODEL_PAGE":
  case "TIER1_PAGE":
  case "TIER1_YEAR_MAKE_MODEL_PAGE":
  case "TIER2_PAGE":
  case "TIER2_YEAR_MAKE_MODEL_PAGE":
  case "TIER3_PAGE":
  case "TIER0_MAKE_MODEL_PAGE":
  case "TIER1_MAKE_MODEL_PAGE":
  case "TIER2_MAKE_MODEL_PAGE":
  case "TIER3_MAKE_MODEL_PAGE":
  case "TIER3_YEAR_MAKE_MODEL_PAGE":
    return (
      <CategoryTierContainer
        {...{
          staticContent,
          actualResponse,
          pageType,
          isSSR,
          urlInformation,
          serverRequestUrl,
          locale,
        }}
      />
    );

    /** *************** Site Structure PAGES **************** */
  case PAGE_TYPES.MAKE_PAGE:
  case PAGE_TYPES.MAKE_MODEL_PAGE:
    return (
      <MakeMakeModelPage
        {...{
          pageType, actualResponse, urlInformation, staticContent,
        }}
      />
    );
  case PAGE_TYPES.YEAR_MAKE_MODEL_PAGE:
    return (
      <MakeModelYearPage
        actualResponse={actualResponse}
        staticContent={staticContent}
        urlInformation={urlInformation}
      />
    );

  case "TIER3_MAKE_PAGE":
  case "TIER2_MAKE_PAGE":
  case "TIER1_MAKE_PAGE":
    return (
      <MakeTierPage
        {...{
          actualResponse, urlInformation, staticContent, pageType,
        }}
      />
    );

    /** *************** All Makes and All manufacture **************** */
  case PAGE_TYPES.ALL_MAKES_PAGE:
  case PAGE_TYPES.ALL_MANUFACTURERS_PAGE:
    return (
      <AllMakeManufacture
        {...{ pageType, actualResponse }}
      />
    );

    /** *************** Single ManufacturePage **************** */
  case PAGE_TYPES.MANUFACTURER_PAGE:
    return (
      <ManufacturerContainer
        {...{
          pageType, actualResponse, urlInformation, staticContent, isSSR,
        }}
      />
    );
    /** *************** Manufacture Category/Collection PAGES **************** */
  case PAGE_TYPES.MANUFACTURER_TIER1_PAGE:
  case PAGE_TYPES.MANUFACTURER_TIER2_PAGE:
    return (
      <ManufacturerTierContainer
        {...actualResponse}
        {...{ staticContent, pageType }}
      />
    );
  case "LANDING_PAGE":
    return (
    // https://partsavatar.ca/p000a-obd-ii-trouble-code-camshaft-position-sensor-pcm-oil-control-valve-solution-timing-chain
      <LandingPage
        pageType={pageType}
        landingPage={deepFind(actualResponse, "landingPage")}
      />
    );
  default:
    return <NotFound />;
  }
}

const DynamicPage = NoMatch;

// DynamicPage.getInitialProps = async ({
//   store,
//   query,
//   res,
// }: any) => {
//   const { getInitialProps } = await import("../modules/pathName");
//   return getInitialProps({ store, query, res });
// };

// Replaces undefined values with null
// const replacer = (key, value: any) => (typeof value === "undefined" ? null : value);

export const getServerSideProps = wrapper.getServerSideProps((store) => async ({
  query, res, locale, req,
}) => {
  const { getInitialProps } = await import("../modules/pathName");
  const { pathname } = query;
  const filteredQuery = { ...query };
  let localesToLoad = ["common", "category-map"];
  const BLOCKED_EXTENSIONS = [
    "php", "yaml", "yml", "xml", "json", "png", "jpg", "jpeg", "gif", "webp", "ico", "svg", "css", "js", "txt",
  ];

  // Remove selected vehicle query params for OEM Page
  if (pathname === "parts") {
    delete filteredQuery.selectedMake;
    delete filteredQuery.selectedModel;
    delete filteredQuery.selectedYear;
  }

  const isBlockedExtension = BLOCKED_EXTENSIONS.some((ext) => pathname?.endsWith(`.${ext}`));
  if (pathname === "undefined" || isBlockedExtension) {
    const ssrTranslations = await serverSideTranslations(locale, localesToLoad, nextI18nextConfig);
    ssrTranslations._nextI18Next.initialI18nStore = {
      [locale]: ssrTranslations._nextI18Next.initialI18nStore[locale],
    };

    return {
      props: {
        ...ssrTranslations,
        pageNotFound: true,
      },
    };
  }

  const ssrProps = await getInitialProps({
    store, query: filteredQuery, res, locale, req,
  });

  if (ssrProps.staticData?.isShopByCategory) {
    localesToLoad.push("category");
  }
  if (PRODUCT_PAGE_TYPES.includes(ssrProps.pageType)) {
    localesToLoad.push("product", "shopping-cart");
  } else if (COLLECTION_PAGE_TYPES.includes(ssrProps.pageType)) {
    localesToLoad = localesToLoad.concat(["category", "product"]);
  } else if (["STATIC_PAGE"].includes(ssrProps.pageType) && (PAGE_WITH_HP_UI.includes(pathname) || COMPETITOR_PAGES.includes(pathname))) {
    localesToLoad.push("home", "category-map");
  } else if (["STATIC_PAGE", "LANDING_PAGE"].includes(ssrProps.pageType)) {
    localesToLoad.push("static-pages");
  } else if (ssrProps.pageType === "MANUFACTURER_PAGE") {
    localesToLoad = localesToLoad.concat(["category-map-t2", "category", "product"]);
  } else if (LINE_PAGES.includes((ssrProps.pageType)) || ssrProps.pageType === "TIER1_MAKE_PAGE") {
    localesToLoad.push("category");
    localesToLoad.push("product");
  }
  const ssrTranslations = await serverSideTranslations(locale, localesToLoad, nextI18nextConfig);
  ssrTranslations._nextI18Next.initialI18nStore = {
    [locale]: ssrTranslations._nextI18Next.initialI18nStore[locale],
  };
  return {
    props: {
      locale,
      ...ssrTranslations,
      ...ssrProps,
    },
  };
});

// NextJS example: https://github.com/vercel/next.js/blob/canary/examples/with-redux-wrapper/pages/other.js
export default DynamicPage;
export const config = { amp: false };
